/** Organisations — the shapes the API will return, standing in for it.
 *
 *  WHY FIXTURES AND NOT A STUB ENDPOINT. The console is being built before its
 *  backend on purpose (stage A of the A→B→C port), and these types are the
 *  thing that survives: **when the endpoints are written, they are written to
 *  return exactly this.** Swapping to live data is one line per page — the
 *  `useX()` hook becomes a `useQuery` and nothing above it changes.
 *
 *  STAGE A MEANS FABRIC'S SHAPE, AND THIS FILE IS DELIBERATELY SMALL. Fabric's
 *  `Company` is an id, a name, a description and a status — nothing else. An
 *  earlier draft carried `party_roles` and `sponsored_by` here; both are
 *  stage-C designs (§9c party roles, D-86 sponsorship) and were imported
 *  early, which is exactly the mixing the A→B→C split exists to prevent.
 *  They come back when they are actually built, with the backend that sets
 *  them.
 *
 *  So: an organisation is a NAME that can have USERS. Everything else it might
 *  later be — a customer, a maker, somebody's sponsored repairer — is derived
 *  or granted elsewhere, and none of it belongs on the create form.
 */

/** A company, as Fabric has one. */
export type Organisation = {
  organisation_id: string;
  name: string;
  description: string | null;
  /** Products allocated to it. Empty = allocated nothing yet. */
  entitlements: { product: string; plan: string | null }[];
  users: number;
  status: "active" | "inactive";
  created_at: string;
  /** Who created it. Every company in production was created by a PERSON in an
   *  admin UI — there is no non-human provisioning path, so this is never a
   *  service account. */
  created_by: string;
};

/** A person, as Fabric has one.
 *
 *  ONE role, not a set — Fabric's `USER_ROLE` is
 *  `super_admin | company_admin | company_user`. An earlier draft carried
 *  `roles: {product, role}[]`, the per-product SET from §9g; that is a stage-C
 *  design and no backend sets it. It comes back when it is built.
 *
 *  `organisation_id` is null for a super admin: `create_user` refuses a super
 *  admin WITH a company and a non-super WITHOUT one, both ways.
 *
 *  What a person may REACH is space access, granted separately — Fabric's
 *  `UserSpaceAccess`, and its own routes on this page. */
export type OrgUser = {
  user_id: string;
  login_id: string;
  name: string;
  organisation_id: string | null;
  role: "super_admin" | "company_admin" | "company_user";
  /** Which (product, space) allocations this person may enter. */
  space_access: { product: string; space: string }[];
  mfa: boolean;
  status: "active" | "deactivated";
  created_at: string;
  created_by: string;
  last_seen: string | null;
};

// ---------------------------------------------------------------- the data --

export const ORGANISATIONS: Organisation[] = [
  { organisation_id: "CMP-001", name: "Aphelion Energy", description: "Platform operator",
    entitlements: [{ product: "Fleet", plan: "Pro +" }, { product: "Connected Cluster", plan: null }],
    users: 12, status: "active", created_at: "2026-05-02", created_by: "system · bootstrap" },
  { organisation_id: "CMP-002", name: "GO Electric", description: "Fleet operator, Pune",
    entitlements: [{ product: "Fleet", plan: "Pro" }, { product: "Connected Cluster", plan: null }],
    users: 8, status: "active", created_at: "2026-05-14", created_by: "dattatraylondhe@aphelionenergy.com" },
  { organisation_id: "CMP-004", name: "Batterysmart", description: "Swap network",
    entitlements: [{ product: "Fleet", plan: "Basic" }], users: 5, status: "active", created_at: "2026-06-21", created_by: "dattatraylondhe@aphelionenergy.com" },
  { organisation_id: "CMP-011", name: "Nexcell Pack Works", description: "Pack manufacturing and repair",
    entitlements: [{ product: "Warranty Insights", plan: "Standard" }], users: 4, status: "active", created_at: "2026-08-03", created_by: "krisha.chhadawa@aphelionenergy.com" },
  { organisation_id: "CMP-014", name: "Pune Service Hub", description: "Authorised service centre",
    entitlements: [], users: 2, status: "active", created_at: "2026-08-19", created_by: "krisha.chhadawa@aphelionenergy.com" },
  { organisation_id: "CMP-021", name: "EVE Energy", description: "Cell supplier",
    entitlements: [], users: 0, status: "active", created_at: "2026-09-01", created_by: "dattatraylondhe@aphelionenergy.com" },
  { organisation_id: "CMP-022", name: "Sinowatt Cells", description: null,
    entitlements: [], users: 0, status: "active", created_at: "2026-09-01", created_by: "dattatraylondhe@aphelionenergy.com" },
  { organisation_id: "CMP-027", name: "Kolhapur Assembly", description: "Contract assembly",
    entitlements: [], users: 0, status: "inactive", created_at: "2026-09-10", created_by: "krisha.chhadawa@aphelionenergy.com" },
];

export const ORG_USERS: OrgUser[] = [
  { user_id: "u-001", login_id: "dattatraylondhe@aphelionenergy.com", name: "Dattatray Londhe",
    organisation_id: null, role: "super_admin", space_access: [], mfa: true, status: "active", created_at: "2026-05-02", created_by: "system · bootstrap", last_seen: "2026-09-25 09:08" },
  { user_id: "u-101", login_id: "anita@nexcell.in", name: "Anita Deshmukh", organisation_id: "CMP-011",
    role: "company_admin", space_access: [{ product: "Connected Cluster", space: "EOL" },
      { product: "Warranty Insights", space: "Default" }], mfa: true, status: "active", created_at: "2026-08-03", created_by: "krisha.chhadawa@aphelionenergy.com", last_seen: "2026-09-24 11:55" },
  { user_id: "u-102", login_id: "sameer@nexcell.in", name: "Sameer Rao", organisation_id: "CMP-011",
    role: "company_user", space_access: [{ product: "Connected Cluster", space: "EOL" }],
    mfa: true, status: "active", created_at: "2026-08-03", created_by: "anita@nexcell.in", last_seen: "2026-09-25 08:40" },
  { user_id: "u-103", login_id: "farah@nexcell.in", name: "Farah Qureshi", organisation_id: "CMP-011",
    role: "company_user", space_access: [{ product: "Connected Cluster", space: "EOL" }],
    mfa: false, status: "active", created_at: "2026-08-05", created_by: "anita@nexcell.in", last_seen: "2026-09-25 09:12" },
  { user_id: "u-104", login_id: "vikram@nexcell.in", name: "Vikram Joshi", organisation_id: "CMP-011",
    role: "company_admin", space_access: [{ product: "Connected Cluster", space: "R&D testing" }],
    mfa: true, status: "deactivated", created_at: "2026-08-05", created_by: "anita@nexcell.in", last_seen: "2026-09-19 17:03" },
  { user_id: "u-201", login_id: "ravi@goelectric.in", name: "Ravi Kumar", organisation_id: "CMP-002",
    role: "company_admin", space_access: [{ product: "Fleet", space: "Default" }], mfa: true, status: "active", created_at: "2026-05-14", created_by: "dattatraylondhe@aphelionenergy.com", last_seen: "2026-09-25 07:20" },
  { user_id: "u-202", login_id: "meera@goelectric.in", name: "Meera Nair", organisation_id: "CMP-002",
    role: "company_user", space_access: [{ product: "Fleet", space: "Default" }], mfa: false, status: "active", created_at: "2026-06-02", created_by: "ravi@goelectric.in", last_seen: "2026-09-23 16:40" },
  { user_id: "u-301", login_id: "tech@puneservice.in", name: "Imran Sheikh", organisation_id: "CMP-014",
    role: "company_user", space_access: [], mfa: true, status: "active", created_at: "2026-08-19", created_by: "krisha.chhadawa@aphelionenergy.com", last_seen: null },
];

/** The words a person uses for Fabric's three role slugs. */
export const ROLE_LABEL: Record<OrgUser["role"], string> = {
  super_admin: "Super admin",
  company_admin: "Company admin",
  company_user: "Company user",
};

/** Stand-ins for the queries. When the endpoints land these become `useQuery`
 *  and every call site is unchanged. */
export function useOrganisations(): Organisation[] { return ORGANISATIONS; }
export function useOrgUsers(): OrgUser[] { return ORG_USERS; }

/** People in an organisation, split by role. DERIVED by counting — a stored
 *  tally is a number that can disagree with the rows it describes. */
export function useOrgUserBreakdown(orgId: string): { admins: number; users: number; total: number } {
  const mine = ORG_USERS.filter((u) => u.organisation_id === orgId);
  return {
    admins: mine.filter((u) => u.role === "company_admin").length,
    users: mine.filter((u) => u.role === "company_user").length,
    total: mine.length,
  };
}
